import { fetchTrack, fetchAudioToken, resolveAudioSource, getAudioKey } from '~/utils/audioplayer';
import { applyVolume, resumeAudio } from '~/utils/audioGain';
import { defineStore } from '#imports';

const VOLUME_KEY = 'ase-player-volume'

function readStoredVolume() {
  try {
    const v = parseFloat(window.localStorage.getItem(VOLUME_KEY))
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1
  } catch (e) {
    return 1
  }
}

export const useAudioPlayerStore = defineStore('audioplayer', {
  state: () => {
    return {
      audioPlayerToken: undefined,
      isPlaying: false,
      loadedProductDetails: undefined,
      loadedWaveSurfer: null,
      showFooterBar: false,
      footerBarVisible: false,
      shouldPlay: false,
      audioErrorMessage: '',
      userClickedPlay: false,
      triggeredByScroll: false,
      // Position in the product's demo list the footer plays
      demoIndex: 0,
      // Play state to keep when the demo changes (null: no demo switch pending)
      demoSwitchPlay: null,
      // Listener volume 0..1, applied on top of the loudness normalization
      volume: 1,
    }
  },
  getters: {
    getAudioPlayerToken(state) {
      return state.audioPlayerToken
    },
    getLoadedWaveSurfer(state) {
      return state.loadedWaveSurfer
    },
  },
  actions: {
    loadVolume() {
      this.volume = readStoredVolume()
    },
    setVolume(volume) {
      this.volume = Math.min(1, Math.max(0, Number(volume) || 0))
      applyVolume(this.volume)
      try {
        window.localStorage.setItem(VOLUME_KEY, String(this.volume))
      } catch (e) {}
    },
    setAudioPlayerToken(token) {
      this.audioPlayerToken = token
    },
    setProductDetails(productDetails) {
      this.resetDemoIndexFor(productDetails)
      this.loadedProductDetails = productDetails
    },
    // A different product starts at its first demo
    resetDemoIndexFor(productDetails) {
      if (getAudioKey(productDetails) !== getAudioKey(this.loadedProductDetails)) {
        this.demoIndex = 0
        this.demoSwitchPlay = null
      }
    },
    // Switch to another demo of the loaded product, keeping play or pause unless play is given.
    // Wraps around at both ends.
    selectDemo(index, play) {
      const count = this.loadedProductDetails?.audioDemos?.length ?? 0
      if (count < 2) {
        return
      }
      index = ((index % count) + count) % count
      if (index === this.demoIndex) {
        return
      }
      this.demoSwitchPlay = play ?? this.isPlaying
      this.demoIndex = index
    },
    setLoadedWaveSurfer(waveSurfer) {
      if (this.loadedWaveSurfer) {
        this.loadedWaveSurfer.destroy()
      }
      this.loadedWaveSurfer = waveSurfer
    },
    async fetchTrack(trackId, retryCount) {

      let retryAttempt = retryCount ?? 0;

      if (!this.audioPlayerToken || retryAttempt > 0) {
        await fetchAudioToken()
      }

      let idType = 'track'
      // get track id type from this.loadedProductDetails
      if (this.loadedProductDetails?.soundcloudIdType) {
        idType = this.loadedProductDetails.soundcloudIdType
      }

      if (this.audioPlayerToken) {
        const result = await fetchTrack(trackId, idType)
        if (result) {
          return result
        }
      }
      retryAttempt += 1

      if (retryAttempt > 2) {
        console.error('audio error. Too many retries.');
        return;
      }

      return this.fetchTrack(trackId, retryAttempt);
    },
    async fetchTrackWithHandling(trackId, productDetails, retryCount = 0) {
      const index = this.demoIndex
      const source = await resolveAudioSource(productDetails, () => this.fetchTrack(trackId, retryCount), index)
      const url = source?.url
      // CDN demos are not probed, SoundCloud proxy URLs are
      const verified = source?.source === 'cdn' || await this.verifyAudioUrl(url)
      // Another track was requested while this one was loading: drop the stale result
      if (getAudioKey(this.loadedProductDetails) !== getAudioKey(productDetails) || this.demoIndex !== index) {
        return null
      }
      this.audioErrorMessage = ''
      if (!url || !verified) {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
          this.loadedWaveSurfer.destroy()
          this.loadedWaveSurfer = null
        }
        this.isPlaying = false
        console.log('fetchTrackWithHandling 1', productDetails)
        this.loadedProductDetails = productDetails || this.loadedProductDetails
        this.showFooterBar = true
        this.footerBarVisible = true
        this.audioErrorMessage = 'Audio file unavailable. Please try again later.'
        return null
      }
      this.loadedProductDetails = productDetails
      console.log('fetchTrackWithHandling 2', productDetails)
      this.isPlaying = false
      // A demo switch keeps the previous play state, a new product plays unless loaded by scrolling
      this.shouldPlay = this.demoSwitchPlay ?? !this.triggeredByScroll
      this.demoSwitchPlay = null
      this.audioErrorMessage = ''
      return source
    },
    async checkTrackAvailability(trackId, idType) {

      if (idType) {
        this.setProductDetails({ soundcloudIdType: idType })
      }

      try {
        const url = await this.fetchTrack(trackId, 0)
        if (!url) return false;
        const verifiedUrl = await this.verifyAudioUrl(url)
        return !!verifiedUrl
      } catch (e) {
        return false
      }
    },
    async verifyAudioUrl(url) {
      try {
        const resp = await fetch(url, { method: 'HEAD', mode: 'cors' })
        if (resp.type === 'opaque') return true
        if (resp.ok) return true
        try {
          const respGet = await fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, mode: 'cors' })
          if (respGet.type === 'opaque') return true
          return respGet.ok
        } catch (eHeadGet) {
          return false
        }
      } catch (e) {
        try {
          const resp = await fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, mode: 'cors' })
          if (resp.type === 'opaque') return true
          return resp.ok
        } catch (e2) {
          return false
        }
      }
    },
    togglePlay(setPlaying = false) {
      if (!this.loadedWaveSurfer) {
        this.shouldPlay = true
        this.footerBarVisible = true
        this.showFooterBar = true
        return
      }
      if (setPlaying === true) {
        resumeAudio()
        this.loadedWaveSurfer.play();
        this.isPlaying = true;
        this.shouldPlay = true;
        return
      }
      if (this.isPlaying) {
        this.loadedWaveSurfer.pause()
        this.isPlaying = false
        return
      } else {
        resumeAudio()
        this.loadedWaveSurfer.play()
        this.isPlaying = true
        this.shouldPlay = true
        return
      }
    },
    async loadFooterAudio(productDetails, shouldPlay = false, triggeredByScroll = false) {
      this.resetDemoIndexFor(productDetails);
      this.loadedProductDetails = productDetails;
      this.showFooterBar = true;
      this.footerBarVisible = true;
      this.shouldPlay = shouldPlay && !triggeredByScroll;
      this.isPlaying = false;
      this.audioErrorMessage = '';
      this.triggeredByScroll = triggeredByScroll;
    },
    destroyLoadedWaveSurfer() {
      this.loadedWaveSurfer?.destroy()
    },
    makeFooterBarVisible() {
      this.footerBarVisible = true;
    },
    hideFooterBar() {
      this.footerBarVisible = false;
    },
    pauseOnly() {
      try {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
        }
      } catch (e) {}
      this.isPlaying = false
    },
    stopPlayback(hideFooter = false, broadcast = true) {
      try {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
          this.loadedWaveSurfer.destroy()
          this.loadedWaveSurfer = null
        }
      } catch (e) {}
      this.isPlaying = false
      this.shouldPlay = false
      this.audioErrorMessage = ''
      this.userClickedPlay = false
      if (hideFooter) {
        this.footerBarVisible = false
        this.showFooterBar = false
      }
    },
    setUserClickedPlay(value) {
      this.userClickedPlay = value
    },
  },
});